import { useEffect, useMemo, useRef } from "react";
import { Button, Typography } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { Commitment } from "design-scope";
import ScopeDesignPackageRow from "./scope-design-package-row";
import { blankCommitment, Section } from "./scope-item-field";
import type { ProjectEntities } from "../hooks/use-project-entities";
import {
  defaultLodIdFromOptions,
  lodForOptionId,
  type LevelOfDetailOption
} from "../hooks/use-level-of-detail";

export type ScopeDesignPackageCommitmentsEditorProps = {
  commitments: Commitment[];
  onCommitmentsChange: (next: Commitment[]) => void;
  entities: ProjectEntities;
  lodOptions: LevelOfDetailOption[];
  lodOptionsLoading?: boolean;
  /** When true, blank commitment rows get a default LOD id once options load. */
  active?: boolean;
  emptyMessage?: string;
  addTestId?: string;
};

/** Commitments that have a design package selected (persistable rows). */
export function pendingDesignPackageCommitments(
  commitments: Commitment[]
): Commitment[] {
  return commitments.filter((c) => c.designPackageId);
}

/** True when LOD deps allow saving / linking the pending DP rows. */
export function isDesignPackageCommitmentsReady(
  commitments: Commitment[],
  lodOptionsLoading: boolean | undefined,
  lodOptionsCount: number
): boolean {
  const pending = pendingDesignPackageCommitments(commitments);
  if (lodOptionsLoading) return false;
  if (pending.length === 0) return true;
  if (lodOptionsCount === 0) return false;
  return pending.every((c) => Boolean(c.levelOfDetailValueId));
}

function AddButton(props: { testId: string; onClick: () => void }) {
  return (
    <Button
      size="small"
      icon={<PlusOutlined />}
      data-testid={props.testId}
      onClick={props.onClick}
    >
      Add
    </Button>
  );
}

/**
 * Controlled "Link Design package" section: + Add, commitment cards, empty
 * state. Shared by Create/Edit Scope Item and Bulk Link to Design Package.
 */
export default function ScopeDesignPackageCommitmentsEditor(
  props: ScopeDesignPackageCommitmentsEditorProps
) {
  const {
    commitments,
    onCommitmentsChange,
    entities,
    lodOptions,
    lodOptionsLoading,
    active = true,
    emptyMessage = "No design packages linked yet.",
    addTestId = "commit-add"
  } = props;

  const commitmentsRef = useRef(commitments);
  commitmentsRef.current = commitments;
  const onCommitmentsChangeRef = useRef(onCommitmentsChange);
  onCommitmentsChangeRef.current = onCommitmentsChange;

  const defaultLodId = useMemo(
    () => defaultLodIdFromOptions(lodOptions),
    [lodOptions]
  );

  // Once LOD options arrive, fill blank commitment rows that still lack an id
  // (open-before-options, or seed without levelOfDetailValueId). Re-run when a
  // row is still blank so a later form reset is not left without a default.
  const needsDefaultLod = commitments.some((c) => !c.levelOfDetailValueId);
  useEffect(() => {
    if (!active || !defaultLodId || !needsDefaultLod) return;
    const cs = commitmentsRef.current;
    let changed = false;
    const next = cs.map((c) => {
      if (c.levelOfDetailValueId) return c;
      changed = true;
      return {
        ...c,
        levelOfDetailValueId: defaultLodId,
        lod: lodForOptionId(lodOptions, defaultLodId)
      };
    });
    if (changed) onCommitmentsChangeRef.current(next);
  }, [active, defaultLodId, lodOptions, needsDefaultLod]);

  const dpOptions = entities.designPackages.map((d) => ({
    value: d.id,
    label: d.label
  }));
  const lodDisabled = Boolean(lodOptionsLoading) || lodOptions.length === 0;

  const patchCommit = (i: number, patch: Partial<Commitment>) => {
    onCommitmentsChange(
      commitments.map((c, idx) => (idx === i ? { ...c, ...patch } : c))
    );
  };

  const setCommitmentLod = (i: number, levelOfDetailValueId: string) => {
    patchCommit(i, {
      levelOfDetailValueId,
      lod: lodForOptionId(lodOptions, levelOfDetailValueId)
    });
  };

  return (
    <Section
      title="Link Design package"
      action={
        <AddButton
          testId={addTestId}
          onClick={() =>
            onCommitmentsChange([
              ...commitments,
              blankCommitment(
                defaultLodId,
                lodForOptionId(lodOptions, defaultLodId)
              )
            ])
          }
        />
      }
    >
      <div className="flex flex-col gap-3">
        {commitments.map((c, i) => (
          <ScopeDesignPackageRow
            key={i}
            index={i}
            commitment={c}
            dpOptions={dpOptions}
            milestoneOptions={(
              entities.milestonesByDp[c.designPackageId] ?? []
            ).map((m) => ({ value: m.id, label: m.label }))}
            lodOptions={lodOptions}
            lodOptionsLoading={lodOptionsLoading}
            lodDisabled={lodDisabled}
            onPatch={(patch) => patchCommit(i, patch)}
            onLodChange={(id) => setCommitmentLod(i, id)}
            onRemove={() =>
              onCommitmentsChange(commitments.filter((_, idx) => idx !== i))
            }
          />
        ))}
        {commitments.length === 0 && (
          <Typography.Text type="secondary">{emptyMessage}</Typography.Text>
        )}
      </div>
    </Section>
  );
}
